import {GetDefaultCameraTemplateGraphGQL} from "@app/common/components/viewers/configurator/extensions/bd-cloud/bd-cloud.generated"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {deserializeGraphNode, serializedGraphNodeSchema} from "@cm/graph-v3"
import {Matrix4, Vector3} from "@cm/math"
import {loadDefaultCamera} from "@cm/mattress-nodes"
import {Camera, defaultsForToneMapping, getOwnedNodes, isTemplateNode, SceneNodes, TemplateGraph} from "@cm/template-nodes"

/** Namespace the template nodes are registered in, see registerTemplateGraphNode. */
const TEMPLATE_NAMESPACE = "template"

/**
 * The camera of the template, which is expected to have at most one camera in its root.
 */
const getRootCamera = (templateGraph: TemplateGraph): Camera | undefined => {
    const cameras = getOwnedNodes(templateGraph, Camera)
    if (cameras.length > 1) throw new Error("The template contains more than one camera in its root")
    return cameras[0]
}

/**
 * Creates a camera node from the camera the user is looking through. Lens, clipping, shift, resolution, pose and target are taken from it; the
 * remaining parameters use the same defaults as adding a camera in the template editor (the values the view camera carries for them describe a
 * viewer camera, not a camera to render with).
 */
const cameraNodeFromSceneCamera = (sceneCamera: SceneNodes.Camera, name = "Camera"): Camera => {
    const {transform, target, nearClip, farClip, filmGauge, focalLength, shiftX, shiftY, resolutionX, resolutionY} = sceneCamera
    return new Camera({
        parameters: {
            name,
            target: target.toArray(),
            targeted: true,
            lockedTransform: transform.toArray(),
            visible: true,
            resolutionX,
            resolutionY,
            nearClip,
            farClip,
            filmGauge,
            focalLength,
            shiftX,
            shiftY,
            fStop: 128,
            focalDistance: 100,
            autoFocus: true,
            ev: 0.0,
            automaticVerticalTilt: false,
            enableOrbiting: true,
            enablePanning: true,
            screenSpacePanning: true,
            adaptAspectRatio: true,
            toneMapping: defaultsForToneMapping("pbr-neutral"),
            enableDamping: false,
            dampingFactor: 0.05,
        },
    })
}

/**
 * Bakes the current view of the orbit controls into the camera of the template. The template is expected to have at most one camera in its root; if
 * there is none, a camera is created from the current state of the viewer camera. Baking fixes the pose, so an automatic target of the camera is
 * dropped, it would otherwise override the baked pose when the template is compiled.
 */
export const setCameraToCurrent = (context: ExtensionContext): void => {
    const {sceneManagerService, logger} = context
    const sceneCamera = context.getViewCamera()

    sceneManagerService.modifyTemplateGraph((templateGraph) => {
        const camera = getRootCamera(templateGraph)
        if (camera) {
            const {automaticTarget, ...parameters} = camera.parameters
            if (automaticTarget !== undefined) logger.info("Dropped the automatic target of the camera, the view is fixed to the current pose")
            camera.replaceParameters({...parameters, lockedTransform: sceneCamera.transform.toArray(), target: sceneCamera.target.toArray()})
        } else {
            templateGraph.parameters.nodes.addEntry(cameraNodeFromSceneCamera(sceneCamera))
        }
    })
}

/**
 * Camera parameters holding other nodes should not be returned to the outside
 */
const assertPlainValued = (parameters: Record<string, unknown>): void => {
    for (const [key, value] of Object.entries(parameters))
        if (isTemplateNode(value))
            throw new Error(`Camera parameter '${key}' is connected to a node instead of holding a plain value, which cannot be exported.`)
}

/** Whether a serialized parameter value contains a nested node (or a reference to one), which is what assertPlainValued rules out on the way out. */
const containsSerializedNode = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.some(containsSerializedNode)
    if (typeof value !== "object" || value === null) return false
    if (typeof (value as {$class?: unknown}).$class === "string") return true
    return Object.values(value).some(containsSerializedNode)
}

/**
 * Returns the serialized camera of the template with the pose of the camera the user is currently looking through baked into it. As in
 * setCameraToCurrent, an automatic target of the camera is dropped in favor of the baked pose.
 */
export const getCameraParams = (context: ExtensionContext): string => {
    const {sceneManagerService} = context
    const sceneCamera = context.getViewCamera()
    const templateCamera = getRootCamera(sceneManagerService.$templateGraph())

    let camera: Camera
    if (templateCamera) {
        const {automaticTarget: _automaticTarget, ...parameters} = templateCamera.parameters
        assertPlainValued(parameters)
        camera = new Camera({
            parameters: {...parameters, lockedTransform: sceneCamera.transform.toArray(), target: sceneCamera.target.toArray()},
        })
    } else {
        camera = cameraNodeFromSceneCamera(sceneCamera)
    }

    return JSON.stringify(camera.serialize())
}

/**
 * Moves the view of the user to the pose of the given camera. Modifying the template alone does not suffice, because the viewer only follows the
 * template camera when its values change, which they do not when the same camera is applied again after the user has orbited or panned away.
 */
const applyViewCamera = (camera: Camera, context: ExtensionContext): void => {
    const {lockedTransform, target} = camera.parameters
    if (!Array.isArray(lockedTransform)) return
    context.setViewCamera(Matrix4.fromArray(lockedTransform), Vector3.fromArray(target))
}

/**
 * Applies a camera serialized by getCameraParams to the template, replacing the parameters of its camera entirely.
 */
export const applyCameraParams = (params: string, context: ExtensionContext): void => {
    const {sceneManagerService} = context

    let parsedParams: unknown
    try {
        parsedParams = JSON.parse(params)
    } catch (error) {
        throw new Error(`Camera parameters are not valid JSON: ${error instanceof Error ? error.message : String(error)}`)
    }

    const serializedCamera = serializedGraphNodeSchema.parse(parsedParams)
    if (serializedCamera.$class !== Camera.getNodeClass())
        throw new Error(`Expected serialized parameters of a ${Camera.getNodeClass()}, got '${serializedCamera.$class}'`)
    if (containsSerializedNode(serializedCamera.$parameters))
        throw new Error("The serialized camera references other nodes, which cannot be applied to a template.")

    //Migrates the parameters through the version chain of the camera. Deserialization skips validation, the parameters are validated when they are applied below.
    const deserializedCamera = deserializeGraphNode(serializedCamera, TEMPLATE_NAMESPACE)
    if (!(deserializedCamera instanceof Camera)) throw new Error(`Expected the serialized parameters to deserialize to a camera`)

    sceneManagerService.modifyTemplateGraph((templateGraph) => {
        const camera = getRootCamera(templateGraph)
        //Replacing the parameters instead of the node keeps the identity of the camera, so that entries in the undo history keep referring to it, as well
        //as its position in the node list of the root.
        if (camera) camera.replaceParameters(deserializedCamera.parameters)
        else templateGraph.parameters.nodes.addEntry(new Camera({parameters: deserializedCamera.parameters}))
    })

    applyViewCamera(deserializedCamera, context)
}

export const applyDefaultCamera = async (context: ExtensionContext): Promise<void> => {
    const {sceneManagerService, injector} = context

    const query = injector.get(GetDefaultCameraTemplateGraphGQL)
    const defaultCamera = await loadDefaultCamera(
        async (templateId) => (await fetchThrowingErrors(query)({templateId})).configuratorData?.latestRevision?.graph,
    )

    sceneManagerService.modifyTemplateGraph((templateGraph) => {
        const camera = getRootCamera(templateGraph)
        if (camera) camera.replaceParameters(defaultCamera.parameters)
        else templateGraph.parameters.nodes.addEntry(defaultCamera)
    })

    applyViewCamera(defaultCamera, context)
}
